import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    getMaxVideosInCycle,
    getVideosCreatedInCycle,
    isVideoLimitExceededInCycle,
} from 'js/src/data/account';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';

interface VideoLimitGate {
    /** True when the team has reached its active-video cap (free plan). */
    isOverLimit: boolean;
    /** Show the "video limit reached" upgrade modal. */
    showVideoLimitModal: () => void;
    /**
     * Run `action` only when the team is under its video limit. When over the limit, the upgrade
     * modal is shown instead and `action` is not invoked. Returns whether `action` ran.
     */
    guard: (action: () => void) => boolean;
}

/**
 * Reusable gate for any UI that creates a new video. Centralizes the free-plan video-limit check
 * and the upgrade modal so every entry point (dashboard create, duplicate, translate, GenX,
 * sketch, smart actions, etc.) behaves consistently. The backend enforces the same limit as a
 * backstop, so this is purely for proactive UX.
 */
export function useVideoLimitGate(): VideoLimitGate {
    const dispatch = useDispatch();
    const isOverLimit = useSelector(isVideoLimitExceededInCycle);
    const videosCreated = useSelector(getVideosCreatedInCycle);
    const maxVideos = useSelector(getMaxVideosInCycle);

    const showVideoLimitModal = useCallback(() => {
        analytics.track('Video limit reached', {
            videos_created_in_cycle: videosCreated,
            max_videos_in_cycle: maxVideos,
        });
        dispatch(
            showModal({
                modal_id: MODAL_IDS.FEATURE_LOCK,
                modal_context: {
                    featureName: LOCKED_FEATURES.VIDEO_LIMIT,
                },
            }),
        );
    }, [dispatch, videosCreated, maxVideos]);

    const guard = useCallback(
        (action: () => void) => {
            if (isOverLimit) {
                showVideoLimitModal();
                return false;
            }
            action();
            return true;
        },
        [isOverLimit, showVideoLimitModal],
    );

    return { isOverLimit, showVideoLimitModal, guard };
}
